const NON_TEXT_INPUT_TYPES = ['radio', 'hidden', 'button', 'submit', 'reset', 'file', 'image'];
const FOCUSABLE_NON_TEXT_INPUT_TYPES = ['checkbox'];

function isTextInput(input: HTMLInputElement): boolean {
  return !NON_TEXT_INPUT_TYPES.includes(input.type.toLowerCase()) && 
         !FOCUSABLE_NON_TEXT_INPUT_TYPES.includes(input.type.toLowerCase());
}

function isFocusableInput(input: HTMLInputElement): boolean {
  return !NON_TEXT_INPUT_TYPES.includes(input.type.toLowerCase());
}

function submitForm(): void {
  const formElement = document.querySelector('form');
  if (formElement) {
    const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
    formElement.dispatchEvent(submitEvent);
  }
}

function isLastNonIgnoredInput(index: number, inputs: HTMLInputElement[]): boolean {
  if (index === inputs.length - 1) {
    return true;
  }
  
  const remainingInputs = inputs.slice(index + 1);
  return remainingInputs.every(input => input?.id.includes("ignore"));
}

function isLastFocusableInput(activeElement: HTMLInputElement, allInputs: HTMLInputElement[]): boolean {
  const focusableInputs = allInputs.filter(isFocusableInput);
  const activeIndex = focusableInputs.indexOf(activeElement);
  
  if (activeIndex === -1) return false;
  if (activeIndex === focusableInputs.length - 1) return true;
  
  return focusableInputs.slice(activeIndex + 1).every(input => input?.id.includes("ignore"));
}

function shouldSubmitForm(event: KeyboardEvent | null, activeElement: HTMLInputElement, allInputs: HTMLInputElement[]): boolean {
  return !!(event && isFocusableInput(activeElement) && isLastFocusableInput(activeElement, allInputs));
}

function handleInvalidActiveElement(inputs: HTMLInputElement[]): void {
  inputs[0]?.focus();
}

function handleFormSubmission(event: KeyboardEvent | null): void {
  if (event) {
    event.preventDefault();
  }
  submitForm();
}

function findNextNonIgnoredIndex(currentIndex: number, inputs: HTMLInputElement[]): number {
  let nextIndex = nextIndexFunction(currentIndex, inputs);
  if (inputs[nextIndex]?.id.includes("ignore")) {
    nextIndex = nextIndexFunction(nextIndex, inputs);
  }
  return nextIndex;
}

function navigateToNextInput(
  index: number,
  inputs: HTMLInputElement[],
  event: KeyboardEvent | null
): void {
  const nextIndex = findNextNonIgnoredIndex(index, inputs);
  if (event && nextIndex !== 0) {
    event.preventDefault();
  }
  inputs[nextIndex]?.focus();
}

function hasValidActiveElement(el: HTMLElement | null): el is HTMLInputElement {
  return !!el && typeof (el as HTMLInputElement).type === 'string';
}

function handleNonTextActiveElement(activeElement: HTMLInputElement, allInputs: HTMLInputElement[], event: KeyboardEvent | null): boolean {
  if (shouldSubmitForm(event, activeElement, allInputs)) {
    handleFormSubmission(event);
    return true;
  }
  return !isTextInput(activeElement);
}

function handleTextInputNavigation(activeElement: HTMLInputElement, inputs: HTMLInputElement[], event: KeyboardEvent | null): void {
  const index = inputs.indexOf(activeElement);

  if (index === -1) {
    handleInvalidActiveElement(inputs);
    return;
  }

  const isLast = event && isLastNonIgnoredInput(index, inputs);
  if (isLast) {
    handleFormSubmission(event);
    return;
  }

  navigateToNextInput(index, inputs, event);
}

export function nextInput(event: KeyboardEvent | null): void {
  const allInputs = Array.from(document.getElementsByTagName("input")) as HTMLInputElement[];
  const inputs = allInputs.filter(isTextInput);
  const activeElement = document.activeElement as HTMLInputElement | null;
  
  if (!hasValidActiveElement(activeElement)) {
    handleInvalidActiveElement(inputs);
    return;
  }
  
  if (handleNonTextActiveElement(activeElement, allInputs, event)) return;

  handleTextInputNavigation(activeElement, inputs, event);
}

function nextIndexFunction(index: number, inputs: HTMLInputElement[]): number {
  return index === inputs.length - 1 ? 0 : index + 1;
}
